<template>
    <section class="event-edit-view">
        <div class="modal-header">
            <h3 v-if="event.uuid">Event Edit</h3>
            <h3 v-else>Add Event</h3>
            <p class="required-fields">All fields marked with * are required</p>
        </div>
        <app-form
            ref="eventForm"
            :model="event"
            :rules="rules"
            @submit.native.prevent="submit">
            <grid>
                <grid-item width="12">
                    <app-form-item label="Title" prop="title">
                        <app-input id="title" v-model="event.title" autofocus/>
                    </app-form-item>
                </grid-item>
                <grid-item width="12">
                    <app-form-item label="Description" prop="description">
                        <app-input id="description" v-model="event.description" type="textarea"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="ERN (Event Ref. Number)" prop="ern">
                        <app-input id="ern" v-model="event.ern"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="8">
                    <app-form-item label="Location" prop="location">
                        <app-select
                            id="location"
                            v-model="event.location_id"
                            filterable
                            clearable
                            placeholder="Select">
                            <app-option
                                v-for="item in locations"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id"/>
                        </app-select>
                    </app-form-item>
                </grid-item>
                <grid-item width="4">
                    <app-form-item label="Capacity" prop="capacity">
                        <app-input id="capacity" v-model="event.capacity"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Start Time (i.e. 11:00am)" prop="start_time">
                        <app-input id="start_time" v-model="event.start_time"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="End Time (i.e. 1:00pm)" prop="end_time">
                        <app-input id="end_time" v-model="event.end_time"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Date" prop="date">
                        <app-date-picker
                            id="date"
                            v-model="event.date"
                            title="The user can either key in date using format mm/dd/yyyy or use the calendar modal to select a date"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Visibility" prop="visibility">
                        <app-radio-group v-model="event.visibility" size="medium">
                            <app-radio :label="0">Public</app-radio>
                            <app-radio :label="1">Private</app-radio>
                        </app-radio-group>
                    </app-form-item>
                </grid-item>
            </grid>

            <app-collapse-item title="Advanced Settings" name="1">
                <grid>
                    <grid-item width="4">
                        <app-form-item prop="late_time">
                            <div slot="label">
                                Late Time
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The number of minutes<br >
                                        before a student is marked<br >
                                        as being late for the event.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>

                            <app-input id="late_time" v-model.number="event.late_time">
                                <template slot="append">minutes</template>
                            </app-input>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="4">
                        <app-form-item prop="absent_time">
                            <div slot="label">
                                Absent Time
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The number of minutes<br >
                                        before a student is marked<br >
                                        absent for the event.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-input id="absent_time" v-model.number="event.absent_time">
                                <template slot="append">minutes</template>
                            </app-input>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="4">
                        <app-form-item label="Min Percentage" prop="min_percentage">
                            <div slot="label">
                                Min Percentage
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The minimum percentage<br >
                                        of the event a student has<br >
                                        to be present for credit.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-input id="min_percentage" v-model.number="event.min_percentage">
                                <template slot="append">%</template>
                            </app-input>
                        </app-form-item>
                    </grid-item>
                    <grid-item v-if="isEmpty(event.absent_time) || isEmpty(event.late_time)">
                        <app-alert style="margin-bottom: 15px">
                            If any values are left empty they will be replaced with school defaults.
                        </app-alert>
                    </grid-item>
                    <grid-item v-if="event.visibility == 0" width="4">
                        <app-form-item label="Drop Cutoff" prop="drop_cutoff">
                            <div slot="label">
                                Drop Cutoff
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The number of hours prior <br >
                                        to an event that a student <br >
                                        can still drop the event.<br >
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-input id="drop_cutoff" v-model.number="event.drop_cutoff">
                                <template slot="append">minutes</template>
                            </app-input>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="8">
                        <app-form-item prop="alerts_students">
                            <div slot="label">
                                Alerts Students
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        Should students receive push<br>
                                        notifications at the start of<br>
                                        class if they are not present.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-switch 
                                id="alerts_students" 
                                v-model="event.alerts_students" 
                                active-text="Yes"
                                inactive-text="No"/>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="12">
                        <app-form-item prop="restricted_by">
                            <div slot="label">
                                Restricted To Team
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        If you want to limit which students<br>
                                        can register for an event, you can<br>
                                        select a team they must be in.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>

                            <app-tag v-if="restriction" closable @close="removeRestriction">{{ restriction.name }}</app-tag>
                            <app-button v-else @click="showPicker = true">Pick Team</app-button>

                            <team-picker v-model="showPicker" @selected-team="addRestriction">
                                <template slot="title">Select Team</template>
                            </team-picker>
                        </app-form-item>
                    </grid-item>
                </grid>
            </app-collapse-item>

            <div class="modal-footer">
                <app-button
                    v-if="event.uuid && role <= 6"
                    :border="false"
                    type="danger"
                    size="large"
                    tabindex="1"
                    style="float:left"
                    plain
                    @click="remove">Delete</app-button>
                <app-button
                    :loading="isSaving"
                    type="primary"
                    native-type="submit"
                    size="large">Save</app-button>
            </div>
        </app-form>
    </section>
</template>
<script>
import { mapGetters } from 'vuex'
import { isEmpty } from '@/utilities'
import moment from 'moment'
import Event from '@/store/models/Event'
import Location from '@/store/models/Location'
import Team from '@/store/models/Team'
import TeamPicker from '../Team/TeamPicker'

export default {
    name: 'page-events-detail-edit',
    components: { TeamPicker },
    inject: ['modalOptions'],
    props: ['event_id'],
    data() {
        return {
            isSaving: false,
            advancedSettings: [],
            event: Event.$cloneOrNew(this.event_id),
            rules: {
                title: [
                    {
                        required: true,
                        message: 'Please enter a title',
                        trigger: 'blur'
                    }
                ],
                start_time: [
                    {
                        required: true,
                        message: 'Please enter a start time',
                        trigger: 'blur'
                    },
                    { validator: this.checkTimeFormat, trigger: 'blur' }
                ],
                end_time: [
                    {
                        required: true,
                        message: 'Please enter an end time',
                        trigger: 'blur'
                    },
                    { validator: this.checkTimeFormat, trigger: 'blur' }
                ],
                date: [
                    {
                        required: true,
                        type: 'date',
                        message: 'Please enter the date of event',
                        trigger: 'blur'
                    }
                ]
            },
            showPicker: false
        }
    },
    computed: {
        ...mapGetters(['role']),
        locations() {
            return Location.query()
                .orderBy('name')
                .get()
        },
        restriction() {
            return this.event.restriction || Team.find(this.event.restricted_by)
        }
    },
    mounted() {
        if (this.event.start_time) {
            this.event.start_time = moment(this.event.start_time,
                'HH:mm:ss').format('h:mma')
        }
        if (this.event.end_time) {
            this.event.end_time = moment(this.event.end_time,
                'HH:mm:ss').format('h:mma')
        }
        if (this.event.date) {
            this.event.date = moment(this.event.date).toDate()
        }

        this.getLocations({ page: 0, per_page: 9999 })
    },
    methods: {
        getLocations(params) {
            Location.$fetchAll(params)
        },
        checkTimeFormat(rule, value, callback) {
            let format = /^([01]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])? ?(?:am|pm|AM|PM)/
            if (!format.test(value)) {
                callback(new Error('Format must be: hh:mm am/pm (i.e.12:00pm)'))
            }
            else {
                callback()
            }
        },
        submit() {
            this.$refs['eventForm'].validate(valid => {
                if (valid) {
                    this.isSaving = true
                    this.event.uuid ? this.update() : this.create()
                }
            })
        },
        create() {
            this.event.$create()
                .then(({ data }) => {
                    this.isSaving = false
                    this.modalOptions.modalOpen = false
                    this.$router.push({
                        name: 'events.detail',
                        params: { uuid: data.uuid }
                    })
                })
                .catch(() => {
                    this.isSaving = false
                })
            return false
        },
        update() {
            this.event.$update()
                .then(() => {
                    this.isSaving = false
                    this.modalOptions.modalOpen = false
                })
            return false
        },
        remove() {
            this.$confirm({
                title: 'Confirm',
                message:
                    'Are you sure you want to remove this event? You cannot undo this action.'
            })
                .then(() => {
                    return this.event.$remove()
                })
                .then(() => {
                    this.$router.push({ name: 'events' })
                })
        },
        removeRestriction() {
            this.event.restricted_by = null
            this.event.restriction = null
        },
        addRestriction(team) {
            this.event.restricted_by = team.id
            this.event.restriction = team
        },
        isEmpty
    }
}
</script>

<style lang="stylus">
.class-edit-view
    .el-checkbox.is-bordered
        padding-right: 14px
        padding-left: 12px
        line-height: initial

        .el-checkbox__input
            padding-left: 0px

        span
            padding-left: 7px

    .el-checkbox+.el-checkbox, .el-checkbox.is-bordered+.el-checkbox.is-bordered
        margin-left: 2px

    .app-form-item
        .app-form-item__label
            position: relative
            z-index: 2

        .el-date-editor
            width: 100%
</style>
